/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import XLSX from "xlsx-js-style";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Grid,
  Chip,
  Tabs,
  Tab,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import {
  MdAccessTime,
  MdToday,
  MdAssignment,
  MdTimeline,
  MdLocalCafe,
  MdCheckCircle,
} from "react-icons/md";
import {
  getDailyStatusReport,
} from "services/company/dailyStatus/dailyStatusService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";

// Helpers for status and priority badge styling
const getStatusChipColor = (status) => {
  switch (status?.toLowerCase()) {
    case "done":
    case "completed":
      return "success";
    case "in_progress":
      return "primary";
    case "review":
      return "info";
    case "paused":
    case "blocked":
      return "warning";
    default:
      return "default";
  }
};

const getPriorityChipColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "urgent":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "info";
    default:
      return "default";
  }
};

// Expandable Task Row with Tabular Remarks & Reviews
const TabularTaskRow = ({ task, isDark, theme }) => {
  const [open, setOpen] = useState(false);
  const reviewsList = task.all_reviews_remarks || [];

  const getDueStatusText = () => {
    const today = new Date();
    const due = task.due_date ? new Date(task.due_date) : null;

    if (task.completed_at) {
      const completed = new Date(task.completed_at);
      if (!due) return "Completed";
      const diff = Math.floor((completed - due) / (1000 * 60 * 60 * 24));
      if (diff < 0) return `Completed ${Math.abs(diff)}d early`;
      if (diff === 0) return "Completed on time";
      return `Completed ${diff}d late`;
    }

    if (!due) return "No due date";
    const remaining = Math.floor((due - today) / (1000 * 60 * 60 * 24));
    if (remaining > 0) return `${remaining} days left`;
    if (remaining === 0) return "Due today";
    return `Overdue by ${Math.abs(remaining)} days`;
  };

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          bgcolor: open
            ? isDark
              ? "rgba(255,255,255,0.03)"
              : "#f9fafb"
            : "inherit",
          "& > *": { borderBottom: "unset" },
        }}
      >
        <TableCell padding="checkbox" sx={{ width: 44 }}>
          {reviewsList.length > 0 ? (
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              title={open ? "Hide Remarks" : "View Remarks"}
              sx={{ borderRadius: "4px" }}
            >
              {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} />
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {task.project_name || "General"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {task.title}
          </Typography>
          {task.description && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 280 }}>
              {task.description}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={task.priority?.toUpperCase()}
            size="small"
            color={getPriorityChipColor(task.priority)}
            sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22, borderRadius: "4px" }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={task.status?.toUpperCase()?.replace(/_/g, " ")}
            size="small"
            color={getStatusChipColor(task.status)}
            sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22, borderRadius: "4px" }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {task.time_spent_formatted || "0m"}
          </Typography>
          {task.estimated_hours ? (
            <Typography variant="caption" color="text.secondary" display="block">
              Est: {task.estimated_hours}h
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          <Typography variant="caption" display="block">
            <strong>Start:</strong> {task.start_date ? new Date(task.start_date).toLocaleDateString() : "N/A"}
          </Typography>
          <Typography variant="caption" display="block">
            <strong>Due:</strong> {task.due_date ? new Date(task.due_date).toLocaleDateString() : "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {getDueStatusText()}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip
            label={`${task.remarks_count || 0} Remarks`}
            size="small"
            variant="outlined"
            color={task.remarks_count > 0 ? "primary" : "default"}
            sx={{ fontWeight: 600, fontSize: "0.72rem", height: 22, borderRadius: "4px" }}
          />
        </TableCell>
      </TableRow>

      {/* Expanded Row for Task Remarks & Reviews (Same as Reports Page) */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                my: 1.5,
                p: 2,
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8f9fa",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: "0.85rem" }}>
                Remarks & Reviews for: "{task.title}" ({reviewsList.length})
              </Typography>

              {reviewsList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No remarks or reviews recorded for this task.
                </Typography>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "4px",
                  }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f3f4f6" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Author / Reviewer</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Category / Purpose</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Remark / Review Note</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Deliverable / File</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Submitted At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reviewsList.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{item.author}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.category?.toUpperCase()?.replace("_", " ")}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: "0.68rem", fontWeight: 600, height: 20, borderRadius: "4px" }}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 350, whiteSpace: "pre-wrap", fontSize: "0.8rem" }}>
                            {item.note}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>
                            {item.deliverables ? (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<AttachFileIcon sx={{ fontSize: "0.9rem" }} />}
                                href={item.deliverables}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ textTransform: "none", fontSize: "0.75rem", p: 0 }}
                              >
                                View File
                              </Button>
                            ) : (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                            {item.created_at_formatted || new Date(item.created_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const TodaysStatusReport = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const currentUser = useSelector(selectCurrentUser);
  const userRole =
    getSecureLocalItem("role") ||
    currentUser?.role ||
    currentUser?.role_name ||
    "Company";
  const isCompanyOrAdmin =
    userRole?.toLowerCase() === "company" ||
    userRole?.toLowerCase() === "admin" ||
    userRole?.toLowerCase() === "super_admin";

  // State
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Fetch report data
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = { date: selectedDate };
      if (selectedEmployeeId) {
        params.employee_id = selectedEmployeeId;
      }
      const res = await getDailyStatusReport(params);
      if (res?.data?.meta?.status || res?.data?.code === 200) {
        setReportData(res.data.data);
      } else if (res?.data) {
        setReportData(res.data);
      }
    } catch (err) {
      console.error("Failed to load today's status report:", err);
      showErrorToast(
        err?.response?.data?.meta?.message ||
        "Failed to load status report for this date"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedEmployeeId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const changeDateByDays = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    const y = current.getFullYear();
    const m = String(current.getMonth() + 1).padStart(2, "0");
    const d = String(current.getDate()).padStart(2, "0");
    setSelectedDate(`${y}-${m}-${d}`);
  };

  const isToday = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return selectedDate === `${y}-${m}-${d}`;
  }, [selectedDate]);

  // Extracted data
  const kpis = reportData?.kpis || {};
  const employee = reportData?.employee || {};
  const attendance = reportData?.attendance || null;
  const breaks = reportData?.breaks || [];
  const breaksSummary = reportData?.breaks_summary || {};
  const tasks = reportData?.tasks || [];
  const tasksSummary = reportData?.tasks_summary || {};
  const remarks = reportData?.remarks || [];
  const timeline = reportData?.timeline || [];
  const allEmployees = reportData?.all_employees || [];

  // ==========================================
  // EXCEL EXPORT (Structured & Professional)
  // ==========================================
  const handleExportToExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      const summaryBlock = [
        ["TODAYS STATUS REPORT", ""],
        ["Report Date", selectedDate],
        ["Employee Code", employee?.employee_code || "—"],
        ["Employee Name", employee?.name || "—"],
        ["Department", employee?.department || "—"],
        ["Designation", employee?.designation || "—"],
        ["Shift", `${employee?.shift_name || "General Shift"} (${(employee?.shift_start_time || "09:30").slice(0, 5)} - ${(employee?.shift_end_time || "18:30").slice(0, 5)})`],
        ["Status", kpis.current_status?.toUpperCase() || "—"],
        ["Working Time", kpis.worked_time_formatted || "0h 0m"],
        ["Total Breaks", breaksSummary.total_break_formatted || `${breaksSummary.total_break_minutes || 0}m`],
        ["Tasks Completed", `${tasksSummary.completed || 0} / ${tasksSummary.total || 0}`],
        ["Productivity Score", `${kpis.productivity_score || 0}%`],
        [],
      ];

      // Tasks
      const taskHeaders = [
        "Project",
        "Task Title",
        "Priority",
        "Status",
        "Time Spent",
        "Est Hours",
        "Start Date",
        "Due Date",
        "Remarks Count",
      ];
      const taskRows = tasks.map((t) => [
        t.project_name || "General",
        t.title,
        t.priority?.toUpperCase(),
        t.status?.toUpperCase(),
        t.time_spent_formatted || "0m",
        t.estimated_hours || 0,
        t.start_date || "—",
        t.due_date || "—",
        t.remarks_count || 0,
      ]);

      // Breaks
      const breakHeaders = [
        "Break #",
        "Break Type",
        "Start Time",
        "End Time",
        "Duration (Mins)",
        "Remarks / Reason",
      ];
      const breakRows = breaks.map((b, idx) => [
        idx + 1,
        b.type_label,
        b.start_time_formatted || "—",
        b.end_time_formatted || "Ongoing",
        b.duration_minutes || 0,
        b.remarks || "—",
      ]);

      const aoa = [
        ...summaryBlock,
        ["=== TODAYS TASKS DETAIL ==="],
        taskHeaders,
        ...taskRows,
        [],
        ["=== TODAYS BREAKS LOG ==="],
        breakHeaders,
        ...breakRows,
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [
        { wch: 22 },
        { wch: 34 },
        { wch: 14 },
        { wch: 16 },
        { wch: 14 },
        { wch: 14 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Status Report");
      const fileName = `Todays_Status_Report_${employee?.employee_code || "EMP"}_${selectedDate}.xlsx`;
      XLSX.writeFile(wb, fileName);
      showSuccessToast("Excel report downloaded successfully!");
    } catch (err) {
      console.error("Failed to export Excel report:", err);
      showErrorToast("Could not export Excel file. Please try again.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: "auto" }}>
      {/* 1. TOP HEADER & FILTERS BAR (Clean rectangular enterprise header) */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "4px",
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
                Todays Status Report
              </Typography>
              <Chip
                label={
                  kpis.current_status === "on_break"
                    ? "ON BREAK"
                    : kpis.current_status === "clocked_in"
                      ? "PRESENT & ACTIVE"
                      : kpis.current_status === "completed"
                        ? "SHIFT COMPLETED"
                        : "NOT CHECKED IN"
                }
                size="small"
                color={
                  kpis.current_status === "clocked_in"
                    ? "success"
                    : kpis.current_status === "on_break"
                      ? "warning"
                      : "default"
                }
                sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22, borderRadius: "4px" }}
              />
              {isToday && (
                <Chip
                  label="TODAY"
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 700, fontSize: "0.68rem", height: 20, borderRadius: "4px" }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Employee: <strong>{employee?.name || "Employee"}</strong> ({employee?.employee_code || "EMP"}) •{" "}
              {employee?.designation} • {employee?.department}
            </Typography>
          </Grid>

          {/* Filters & Export Action */}
          <Grid item xs={12} md={6}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              flexWrap="wrap"
            >
              {/* Employee switch dropdown for managers/admins */}
              {isCompanyOrAdmin && allEmployees.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="emp-select-label">Employee</InputLabel>
                  <Select
                    labelId="emp-select-label"
                    value={selectedEmployeeId}
                    label="Employee"
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    sx={{ borderRadius: "4px" }}
                  >
                    <MenuItem value="">
                      <em>Current User</em>
                    </MenuItem>
                    {allEmployees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Date Controls */}
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  px: 0.5,
                }}
              >
                <IconButton size="small" onClick={() => changeDateByDays(-1)} title="Previous Day" sx={{ borderRadius: "4px" }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  sx={{
                    width: 140,
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiInputBase-input": { py: 0.5, fontSize: "0.85rem", fontWeight: 600 },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => changeDateByDays(1)}
                  title="Next Day"
                  disabled={selectedDate >= new Date().toISOString().split("T")[0]}
                  sx={{ borderRadius: "4px" }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Stack>

              {/* Excel Export Button */}
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportToExcel}
                sx={{
                  borderRadius: "4px",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 2,
                }}
              >
                Export Excel
              </Button>

              {/* Refresh */}
              <IconButton
                onClick={fetchReport}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                }}
                title="Refresh"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* 2. OVERALL SUMMARY TABLE (Clean Rectangular Enterprise Table) */}
      <Box sx={{ mb: 3 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
          }}
        >
          <Table size="small" aria-label="summary table">
            <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Working Time</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Punch In / Out</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Shift Schedule</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Breaks Logged</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Total Tasks</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Tasks Done</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>In Progress</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Productivity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: "0.95rem" }}>
                  {kpis.worked_time_formatted || "0h 0m"}
                </TableCell>
                <TableCell sx={{ fontSize: "0.82rem" }}>
                  In: {attendance?.check_in_formatted || "—"} | Out: {attendance?.check_out_formatted || (attendance?.check_in ? "Working" : "—")}
                </TableCell>
                <TableCell sx={{ fontSize: "0.82rem" }}>
                  {(employee?.shift_start_time || "09:30").slice(0, 5)} - {(employee?.shift_end_time || "18:30").slice(0, 5)}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                  {breaksSummary.total_break_formatted || `${breaksSummary.total_break_minutes || 0}m`} ({breaksSummary.total_breaks_count || 0})
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                  {tasksSummary.total || 0}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "success.main", fontSize: "0.85rem" }}>
                  {tasksSummary.completed || 0}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "primary.main", fontSize: "0.85rem" }}>
                  {tasksSummary.in_progress || 0}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                  {kpis.productivity_score || 0}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 3. NAVIGATION TABS (Rectangular Tabs) */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => setTabValue(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              minHeight: 44,
              borderRadius: "4px 4px 0 0",
            },
          }}
        >
          <Tab
            icon={<MdAssignment className="mr-1 text-base" />}
            iconPosition="start"
            label={`Tasks Details (${tasks.length})`}
          />
          <Tab
            icon={<MdTimeline className="mr-1 text-base" />}
            iconPosition="start"
            label="Whole Day Scenario"
          />
          <Tab
            icon={<MdLocalCafe className="mr-1 text-base" />}
            iconPosition="start"
            label={`Breaks Log (${breaks.length})`}
          />
        </Tabs>
      </Box>

      {/* TAB 0: TASKS DETAILS (TABULAR FORMAT WITH EXPANDABLE REMARKS) */}
      {tabValue === 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
              Tasks Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expand any task row to view associated remarks, reviews, deliverables, and notes
            </Typography>
          </Stack>

          {tasks.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No tasks active, assigned, or updated for this date.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "4px",
              }}
            >
              <Table aria-label="tasks table">
                <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 44 }} />
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Task Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Time Logged</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Dates</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Schedule</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task) => (
                    <TabularTaskRow
                      key={task.id}
                      task={task}
                      isDark={isDark}
                      theme={theme}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TAB 1: WHOLE DAY'S SCENARIO IN A FRAME (TABULAR TIMELINE SCENARIO) */}
      {tabValue === 1 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
              Whole Day Scenario Flow
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Chronological sequence of punch events, task updates, breaks, and remarks for {selectedDate}
            </Typography>
          </Stack>

          {timeline.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No events or activity recorded for this date yet.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "4px",
              }}
            >
              <Table aria-label="scenario table">
                <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", width: 120 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", width: 140 }}>Event Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Milestone Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Details & Remarks</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.8rem", width: 140 }}>Status / Badge</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeline.map((event, idx) => (
                    <TableRow key={event.id || idx} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                        {event.time || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={event.type.replace(/_/g, " ").toUpperCase()}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            height: 20,
                            borderRadius: "4px",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                        {event.title}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                        {event.description}
                      </TableCell>
                      <TableCell align="right">
                        {event.badge && (
                          <Chip
                            label={event.badge}
                            size="small"
                            variant="outlined"
                            color={
                              event.status === "success"
                                ? "success"
                                : event.status === "warning"
                                  ? "warning"
                                  : event.status === "error"
                                    ? "error"
                                    : "default"
                            }
                            sx={{ fontWeight: 600, fontSize: "0.68rem", height: 20, borderRadius: "4px" }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TAB 2: BREAKS LOG (TABULAR FORMAT) */}
      {tabValue === 2 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
              Breaks Log
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total break duration: {breaksSummary.total_break_formatted || "0m"} ({breaksSummary.total_breaks_count || 0} breaks)
            </Typography>
          </Stack>

          {breaks.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No breaks recorded for this date.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "4px",
              }}
            >
              <Table aria-label="breaks table">
                <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", width: 80 }}>Break #</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Break Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Start Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>End Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>Reason / Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breaks.map((b, idx) => (
                    <TableRow key={b.id || idx} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>#{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                        {b.type_label}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>{b.start_time_formatted || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>{b.end_time_formatted || "Ongoing"}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                        {b.formatted_duration}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={b.is_ongoing ? "ONGOING" : "COMPLETED"}
                          size="small"
                          color={b.is_ongoing ? "error" : "default"}
                          sx={{ fontWeight: 600, fontSize: "0.68rem", height: 20, borderRadius: "4px" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", color: "text.secondary", maxWidth: 400 }}>
                        {b.remarks || "No remarks provided"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

    </Box>
  );
};

export default TodaysStatusReport;
